import React, { useCallback, useMemo } from 'react';
import { ModalContainer, ModalLayout } from '@cp/ds/src/components/modalLayout';
import { MOBILE_BREAKPOINT_THRESHOLD, SxProps } from '@cp/ds';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { useIsMobile } from '@cp/ds/src/hooks';
import { billingTariffApi } from '@cp/entities/tariff';
import { IModalProps } from '@cp/shared/modal';
import { Typography } from '@cp/ds/src/components/typography';
import { FreePeriodLabel, useActivatedPromocodesFreePeriodDuration } from '@cp/entities/promocode';
import capitalize from 'lodash/capitalize';
import { Button } from '@cp/ds/src/components/button';
import { ClientType } from '@common/model/client/clientType';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { formatPromocodeDuration } from '@cp/entities/promocode/model/formatPromocodeDuration';
import { useCurrentCity } from '@cp/entities/location';
import { useClientProfile } from 'src/entities/client';
import { TariffModalResult } from '../model/types';
import { TariffSlider } from './tariffSlider';

export interface TariffModalLayoutProps extends IModalProps<TariffModalResult> {
  tariffId?: number;
}

export const TariffModalLayout = ({ onSuccess, onCancel, tariffId }: TariffModalLayoutProps) => {
  const isLoggedIn = useIsLoggedIn();
  const clientProfile = useClientProfile();
  const isMobile = useIsMobile();
  const { currentCity } = useCurrentCity({ allowEmptyValue: true });
  const { data, isLoading } = billingTariffApi.endpoints.getBillingTariffs.useQuery(
    {
      clientType: clientProfile?.type || ClientType.Employer,
      cityId: clientProfile?.cityId || currentCity?.id,
    },
    { skip: isLoggedIn && !clientProfile?.type },
  );
  const tariffs = useMemo(() => (data?.length === 3 ? [data[0], data[2], data[1]] : data), [data]);
  const activatedPromocodesFreePeriodDuration = useActivatedPromocodesFreePeriodDuration();
  const activatedPromocodesFreePeriod = activatedPromocodesFreePeriodDuration
    ? formatPromocodeDuration(activatedPromocodesFreePeriodDuration)
    : '';
  const activatedPromocodesFreePeriodForButton = activatedPromocodesFreePeriodDuration
    ? formatPromocodeDuration(activatedPromocodesFreePeriodDuration, { wording: 'no-prefix' })
    : '';
  const [selectedTariffId, setSelectedTariffId] = React.useState<number | undefined>(tariffId);

  const onTariffSelect = useCallback((id: number) => setSelectedTariffId(id), []);

  /**
   * Select second tariff by default
   */
  React.useEffect(() => {
    if (selectedTariffId || !tariffs?.length) {
      return;
    }

    setSelectedTariffId(tariffs[1]?.id ?? tariffs[0].id);
  }, [selectedTariffId, tariffs]);

  const description = `${activatedPromocodesFreePeriod ? capitalize(`${activatedPromocodesFreePeriod} - бесплатно. `) : ''}`;

  const contentContainerSx: SxProps = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: { xs: 'stretch', [MOBILE_BREAKPOINT_THRESHOLD]: 'center' },
    maxWidth: { [MOBILE_BREAKPOINT_THRESHOLD]: 550 },
    gap: 2.5,
  };

  return (
    <ModalLayout
      containerSx={{ justifyContent: 'center' }}
      onClose={onCancel}
      title="Выберите длительность подписки"
      titleProps={{ sx: { pb: { xs: 2, md: 2.5 } } }}
    >
      <ModalContainer sx={contentContainerSx}>
        <Typography component="p" sx={{ textAlign: 'center', m: 0 }} variant="body bold">
          {description}
          <br />
          Отменить подписку можно в любой момент.
        </Typography>
      </ModalContainer>

      {isLoading || !tariffs ? (
        <ModalContainer>
          <ContentSkeleton blocks={2} sx={{ width: isMobile ? '100%' : 340 }} />
        </ModalContainer>
      ) : (
        <TariffSlider
          name="tariff"
          onTariffSelect={onTariffSelect}
          options={tariffs}
          promoLabel={activatedPromocodesFreePeriod && <FreePeriodLabel />}
          sx={{ width: { xs: 1, [MOBILE_BREAKPOINT_THRESHOLD]: 'auto' }, py: { xs: 4, md: 5 } }}
          value={selectedTariffId}
        />
      )}

      <ModalContainer sx={contentContainerSx}>
        <Button
          color="pink"
          disabled={!selectedTariffId}
          onClick={() => {
            if (!selectedTariffId) {
              return;
            }
            onSuccess({ tariffId: selectedTariffId });
          }}
          sx={{ px: 2 }}
          variant="primary"
        >
          {activatedPromocodesFreePeriodForButton ? `Попробовать ${activatedPromocodesFreePeriodForButton} бесплатно` : 'Оформить подписку'}
        </Button>
      </ModalContainer>
    </ModalLayout>
  );
};
